import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import styled from 'styled-components';
import { z, ZodRawShape } from 'zod/v3';
import { getIsoDateFromFormattedDate, zIsoDate } from '../../common/utils';
import { ValidationMessagesMap } from '../../common/utils/ValidationMessagesMap';
import { SystemClassifierOption } from '../../configuration';
import { isDateRangesOverlapping } from '../../user/utils/isRangesOverlapping';
import { RequiredKeyDataType, RequiredKeyStatusDataType } from '../schema';
import {
  RequiredKeyDataSchema,
  RequiredKeyStatusDataSchema,
} from '../schema/RequiredKeyDataSchema';
import translations from '../translations/validation.translations.json';
import { BulkEditKey, EditableField } from './types';

const { addTranslations, t } = i18nNsFactory('required-key-utils');
addTranslations(translations);

export function buildBulkSchema(
  fields: Set<EditableField>,
  indicatorStatus: boolean,
) {
  const baseShape = indicatorStatus
    ? RequiredKeyStatusDataSchema.shape
    : RequiredKeyDataSchema.shape;
  const { statusId, ruleTypeId, recipientIds } = baseShape;

  const shape: ZodRawShape = {};
  fields.forEach((field) => {
    switch (field) {
      case 'recipientIds':
        shape['recipientIds'] = recipientIds;
        break;
      case 'ruleTypeId':
        shape['ruleTypeId'] = ruleTypeId;
        break;
      case 'statusId':
        shape['statusId'] = statusId;
        break;
      case 'validFrom':
        shape['validFrom'] = zIsoDate().nullable();
        break;
      case 'validTo':
        shape['validTo'] = zIsoDate().nullable();
        break;
    }
  });

  return z.object(shape);
}

export function createDefaultBulkEdit(
  indicatorStatus: boolean,
  recipientIds: number[],
) {
  const key = {
    recipientIds,
    ruleTypeId: null,
    statusId: null,
    validFrom: null,
    validTo: null,
  };

  if (indicatorStatus) {
    return {
      ...key,
      statusId: null,
    };
  }

  return key;
}

export function bulkUpdateKeys(
  formData: BulkEditKey,
  indexes: Set<number>,
  fields: Set<EditableField>,
  setValue: UseFormSetValue<FieldValues>,
) {
  Object.entries(formData).forEach(([prop, value]) => {
    if (fields.has(prop as EditableField)) {
      indexes.forEach((index) => {
        const isValidFrom = prop === 'validFrom';
        const opts = { shouldDirty: true };
        if (isValidFrom || prop === 'validTo') {
          const date = value ? getIsoDateFromFormattedDate(value as any) : null;
          const periodIndex = isValidFrom ? 0 : 1;
          setValue(
            `requiredKeys.${index}.validPeriod.${periodIndex}`,
            date,
            opts,
          );
        } else {
          setValue(`requiredKeys.${index}.${prop}`, value, opts);
        }
      });
    }
  });
}

export function validateBulkDates(
  formData: BulkEditKey,
  indexes: Set<number>,
  fields: Set<EditableField>,
  getValues: UseFormGetValues<FieldValues>,
  setValidationErrors: (value: ValidationMessagesMap | undefined) => void,
) {
  const messages = new ValidationMessagesMap();
  let valid = true;

  const isFromChanged = fields.has('validFrom');
  const isToChanged = fields.has('validTo');
  if (!isFromChanged && !isToChanged) {
    // Validity not changed
    setValidationErrors(messages);
    return true;
  }

  const keys: RequiredKeyDataType[] = getValues('requiredKeys');
  const updatedPeriods = keys.map((key, index) => {
    const validPeriod = [...getValues(`requiredKeys.${index}.validPeriod`)] as [
      string | null,
      string | null,
    ];
    if (indexes.has(index)) {
      if (isFromChanged) {
        validPeriod[0] = formData.validFrom;
      }
      if (isToChanged) {
        validPeriod[1] = formData.validTo;
      }
    }

    return { key: key.key, validPeriod };
  });

  for (const index of indexes) {
    const validPeriod = updatedPeriods[index].validPeriod;
    const key = keys[index].key;

    if (
      updatedPeriods
        .filter((row, i) => i !== index && row.key === key)
        .some((row) => isDateRangesOverlapping(validPeriod, row.validPeriod))
    ) {
      valid = false;
      messages.append(
        'validFrom',
        t('validation.overlapping_keys', { key }),
        'warning',
      );
      setValidationErrors(messages);
      break;
    }
  }

  return valid;
}

export function createDefaultKey({
  indicatorStatus,
  recipientIds,
  sequenceNumber,
  statusId,
}: {
  indicatorStatus?: boolean;
  recipientIds: number[];
  sequenceNumber: number;
  statusId?: number;
}): RequiredKeyDataType | RequiredKeyStatusDataType {
  const key: RequiredKeyDataType | RequiredKeyStatusDataType = {
    id: null,
    description: null,
    key: '',
    name: null,
    recipientIds,
    relatedAlgorithmIds: [],
    ruleTypeId: null,
    sequenceNumber: sequenceNumber,
    statusId: null,
    validPeriod: [null, null],
    voluntary: false,
  };
  if (indicatorStatus && statusId) {
    key.statusId = statusId;
  }
  return key;
}

export function renderClassifierPreview(
  value?: number | null,
  classifiers?: SystemClassifierOption[],
) {
  return classifiers?.find((item) => item?.value === value)?.label;
}

export function renderClassifiersPreview(
  value?: number[],
  classifiers?: SystemClassifierOption[],
) {
  return classifiers
    ?.filter((recipient) => value?.includes(Number(recipient.value)))
    .map((recipient) => recipient.label)
    .join(', ');
}

export const KeyTableWrapper = styled(Flex)`
  margin: auto;
`;
